// slices/lectures/lecturesSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { LecturePreview } from '../../types/Lecture';
import moment from 'moment';
import { logout } from '../auth/authSlice';

type LecturesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// Numeric comparator for decimal strings (handles negatives and arbitrary precision)
function compareDecimalStrings(
  a: string | number | undefined,
  b: string | number | undefined
): number {
  if (a === b) return 0;
  const as = (a ?? '').toString();
  const bs = (b ?? '').toString();
  if (as === bs) return 0;
  const aNeg = as.startsWith('-');
  const bNeg = bs.startsWith('-');
  if (aNeg !== bNeg) return aNeg ? -1 : 1;

  const aAbs = as.replace(/^[+-]/, '');
  const bAbs = bs.replace(/^[+-]/, '');
  const [aInt, aFrac = ''] = aAbs.split('.');
  const [bInt, bFrac = ''] = bAbs.split('.');

  if (aInt.length !== bInt.length) {
    const cmp = aInt.length - bInt.length;
    return aNeg ? -cmp : cmp;
  }
  const intCmp = aInt.localeCompare(bInt);
  if (intCmp !== 0) return aNeg ? -intCmp : intCmp;

  const maxFrac = Math.max(aFrac.length, bFrac.length);
  const aFracPadded = aFrac.padEnd(maxFrac, '0');
  const bFracPadded = bFrac.padEnd(maxFrac, '0');
  const fracCmp = aFracPadded.localeCompare(bFracPadded);
  return aNeg ? -fracCmp : fracCmp;
}

// Configure the adapter with a sortComparer function
const lecturesAdapter = createEntityAdapter<LecturePreview>({
  // Sort by orderKey lexicographically (opaque string), fallback to createdAt
  sortComparer: (a, b) => {
    const orderCmp = compareDecimalStrings(a.orderKey, b.orderKey);
    if (orderCmp !== 0) return orderCmp;

    const createdAtA = moment(a.createdAt);
    const createdAtB = moment(b.createdAt);
    if (createdAtA.isBefore(createdAtB)) return -1;
    if (createdAtA.isAfter(createdAtB)) return 1;
    return 0;
  },
});

interface LecturesState extends ReturnType<
  typeof lecturesAdapter.getInitialState
> {
  status: LecturesStatus;
  error: string | null;
  selectedLectureIds: string[];
  lastFetchedAt: number | null;
  lastFetchedExamId: string | null;
  loadingExamId: string | null;
  latestRequestId: number | null;
}

const initialState: LecturesState = lecturesAdapter.getInitialState({
  status: 'idle',
  error: null,
  selectedLectureIds: [],
  lastFetchedAt: null,
  lastFetchedExamId: null,
  loadingExamId: null,
  latestRequestId: null,
  // lecturesByExamId: {},
});

const getSelectableLectureIds = (state: LecturesState) =>
  (state.ids as string[]).filter(
    (id) =>
      state.entities[id]?.enabled !== false &&
      state.entities[id]?.hidden !== true
  );

const reconcileSelectedLectures = (state: LecturesState) => {
  const selectableLectureIds = getSelectableLectureIds(state);
  const selectableLectureIdSet = new Set(selectableLectureIds);

  state.selectedLectureIds = state.selectedLectureIds.filter((id) =>
    selectableLectureIdSet.has(id)
  );

  if (
    state.selectedLectureIds.length === 0 &&
    selectableLectureIds.length > 0
  ) {
    state.selectedLectureIds = [selectableLectureIds[0]];
  }
};

// Hydrate the selection for a freshly-loaded exam from the persisted
// `selected` flags on each lecture (the backend is the source of truth per
// exam load). This replaces the old reconcile-from-previous-state behavior,
// which clobbered a valid selection whenever the exam's lecture set changed
// (e.g. switching to STEP 1, which has no lectures).
//
// `hasCustomizedLectureSelection` distinguishes an exam the user has explicitly
// customized (respect the persisted selection verbatim, even when empty) from
// one they've never touched (fall back to auto-selecting the first lecture so
// the panel isn't blank on first visit). The auto-select is ephemeral — it is
// never written back, so it can't resurrect a deliberately-cleared selection.
const hydrateSelectedLecturesForExam = (
  state: LecturesState,
  hasCustomizedLectureSelection: boolean
) => {
  const selectableLectureIds = getSelectableLectureIds(state);
  const selectableLectureIdSet = new Set(selectableLectureIds);

  const persistedSelectedIds = (state.ids as string[]).filter(
    (id) =>
      state.entities[id]?.selected === true && selectableLectureIdSet.has(id)
  );

  if (hasCustomizedLectureSelection || persistedSelectedIds.length > 0) {
    state.selectedLectureIds = persistedSelectedIds;
  } else if (selectableLectureIds.length > 0) {
    state.selectedLectureIds = [selectableLectureIds[0]];
  } else {
    state.selectedLectureIds = [];
  }
};

const upsertManyLectures = (
  state: LecturesState,
  lectures: LecturePreview[]
) => {
  lecturesAdapter.upsertMany(state, lectures);
  state.status = 'succeeded';
  state.error = null;
  state.loadingExamId = null;
  state.lastFetchedAt = Date.now();
  // Track which exam these lectures belong to
  if (lectures.length > 0) {
    state.lastFetchedExamId = lectures[0].examId;
  }
};

const lecturesSlice = createSlice({
  name: 'lectures',
  initialState,
  reducers: {
    lecturesLoading: (
      state,
      action: PayloadAction<{ examId?: string; requestId: number }>
    ) => {
      state.status = 'loading';
      state.error = null;
      state.loadingExamId = action.payload.examId ?? null;
      state.latestRequestId = action.payload.requestId;
    },
    /**
     * Symmetric release for a claim `lecturesLoading` made on a request that
     * never applied (discarded as stale, or failed). Without it
     * `loadingExamId` stays pinned to the abandoned exam and the guard in
     * `lecturesReceivedForExam` drops every later response for a different
     * one — including the refreshes that never dispatch `lecturesLoading`
     * (`preserveSelection`/`skipLoadingState`), which then leaves the flat
     * collection on the previous exam's documents (ORA-498).
     */
    lecturesRequestAbandoned: (
      state,
      action: PayloadAction<{
        requestId: number;
        previousRequestId: number | null;
      }>
    ) => {
      if (state.latestRequestId !== action.payload.requestId) return;
      state.latestRequestId = action.payload.previousRequestId;
      state.loadingExamId = null;
    },
    lecturesReceived: (state, action: PayloadAction<LecturePreview[]>) => {
      lecturesAdapter.setAll(state, action.payload);
      state.status = 'succeeded';
      state.loadingExamId = null;
      state.lastFetchedAt = Date.now();
      // Track which exam these lectures belong to
      if (action.payload.length > 0) {
        state.lastFetchedExamId = action.payload[0].examId;
      }
      reconcileSelectedLectures(state);
    },
    lecturesReceivedForExam: (
      state,
      action: PayloadAction<{
        examId: string;
        lectures: LecturePreview[];
        requestId?: number;
        hasCustomizedLectureSelection?: boolean;
      }>
    ) => {
      // Responses are applied in request order (not by exact match): a
      // background refresh identifies itself with its own id, and dropping it
      // for not being the latest claimed request would leave the list stale.
      if (
        action.payload.requestId !== undefined &&
        state.latestRequestId !== null &&
        action.payload.requestId < state.latestRequestId
      ) {
        return;
      }

      if (
        state.loadingExamId !== null &&
        state.loadingExamId !== action.payload.examId
      ) {
        return;
      }

      if (action.payload.requestId !== undefined) {
        state.latestRequestId = action.payload.requestId;
      }

      lecturesAdapter.setAll(state, action.payload.lectures);
      state.status = 'succeeded';
      state.loadingExamId = null;
      state.lastFetchedAt = Date.now();
      state.lastFetchedExamId = action.payload.examId;

      hydrateSelectedLecturesForExam(
        state,
        action.payload.hasCustomizedLectureSelection ?? false
      );
    },
    /**
     * Append or update many lectures without replacing the entire collection.
     * Useful for pagination where subsequent pages are loaded.
     */
    lecturesUpsertMany: (state, action: PayloadAction<LecturePreview[]>) => {
      upsertManyLectures(state, action.payload);

      // Keep current selection as-is; if empty and we now have items, select first
      if (state.selectedLectureIds.length === 0) {
        const selectableLectureIds = getSelectableLectureIds(state);
        if (selectableLectureIds.length > 0) {
          state.selectedLectureIds = [selectableLectureIds[0]];
        }
      }
    },
    /**
     * Upsert WITHOUT the select-first fallback above. Post-mutation and
     * mapping-job refreshes take this path: a user who deliberately unchecked
     * every document has an empty selection, and auto-selecting the first
     * document there silently adds a never-chosen document to the study plan
     * as soon as anything else is checked (the next persist writes both).
     */
    lecturesUpsertManyPreservingSelection: (
      state,
      action: PayloadAction<LecturePreview[]>
    ) => {
      upsertManyLectures(state, action.payload);
    },
    lectureReceived: (state, action: PayloadAction<LecturePreview>) => {
      lecturesAdapter.upsertOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;

      if (action.payload.enabled === false || action.payload.hidden === true) {
        state.selectedLectureIds = state.selectedLectureIds.filter(
          (id) => id !== action.payload.id
        );
      }
    },
    lectureDeleted: (state, action: PayloadAction<string>) => {
      const deletedLectureId = action.payload;
      lecturesAdapter.removeOne(state, deletedLectureId);
      state.status = 'succeeded';
      state.error = null;

      // Remove from selection if the deleted lecture was selected
      state.selectedLectureIds = state.selectedLectureIds.filter(
        (id) => id !== deletedLectureId
      );
    },
    lecturesRemoved: (state, action: PayloadAction<string[]>) => {
      const deletedLectureIds = new Set(action.payload);
      lecturesAdapter.removeMany(state, action.payload);
      state.status = 'succeeded';
      state.error = null;

      state.selectedLectureIds = state.selectedLectureIds.filter(
        (id) => !deletedLectureIds.has(id)
      );
    },
    lecturesFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load lectures';
      state.loadingExamId = null;
    },
    lectureSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const lectureExists = state.entities[id];
      if (!lectureExists || lectureExists.enabled === false) return;
      if (state.selectedLectureIds.includes(id)) {
        state.selectedLectureIds = state.selectedLectureIds.filter(
          (lecId) => lecId !== id
        );
      } else {
        state.selectedLectureIds.push(id);
      }
    },
    lectureActivated: (state, action: PayloadAction<string>) => {
      // Make a single lecture the sole selection (a plain row click), replacing
      // any multi-selection. Mirrors the Documents page's single-select.
      const id = action.payload;
      const lecture = state.entities[id];
      if (!lecture || lecture.enabled === false) return;
      state.selectedLectureIds = [id];
    },
    lectureDeselected: (state, action: PayloadAction<string | undefined>) => {
      const id = action.payload;
      if (id) {
        state.selectedLectureIds = state.selectedLectureIds.filter(
          (lecId) => lecId !== id
        );
      } else {
        state.selectedLectureIds = [];
      }
    },
    lecturesAllSelected: (
      state,
      action: PayloadAction<string[] | undefined>
    ) => {
      state.selectedLectureIds =
        action.payload ?? getSelectableLectureIds(state);
    },
    lectureStatusUpdated: (
      state,
      action: PayloadAction<{
        lectureId: string;
        enabled?: boolean;
        hidden?: boolean;
      }>
    ) => {
      const { lectureId, enabled, hidden } = action.payload;
      const lecture = state.entities[lectureId];

      if (lecture) {
        lecturesAdapter.updateOne(state, {
          id: lectureId,
          changes: {
            ...(enabled !== undefined ? { enabled } : {}),
            ...(hidden !== undefined ? { hidden } : {}),
          },
        });
      }

      if (enabled === false || hidden === true) {
        state.selectedLectureIds = state.selectedLectureIds.filter(
          (id) => id !== lectureId
        );
      }
    },
    /**
     * Updates the order number of a specific lecture
     * @param state - Current lectures state
     * @param action - Payload containing lectureId and new order number
     */
    lectureOrderUpdated: (
      state,
      action: PayloadAction<{ lectureId: string; ord: number }>
    ) => {
      const { lectureId, ord } = action.payload;
      const lecture = state.entities[lectureId];

      if (lecture) {
        // Update the lecture's order number
        lecturesAdapter.updateOne(state, {
          id: lectureId,
          changes: { ord },
        });

        state.status = 'succeeded';
        state.error = null;
      }
    },
    /**
     * Updates the orderKey of a specific lecture (opaque string)
     */
    lectureOrderKeyUpdated: (
      state,
      action: PayloadAction<{ lectureId: string; orderKey: string }>
    ) => {
      const { lectureId, orderKey } = action.payload;
      const lecture = state.entities[lectureId];
      if (lecture) {
        lecturesAdapter.updateOne(state, {
          id: lectureId,
          changes: { orderKey },
        });

        state.status = 'succeeded';
        state.error = null;
      }
    },
    clearLectures: (state) => {
      lecturesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.selectedLectureIds = [];
      state.lastFetchedAt = null;
      state.lastFetchedExamId = null;
      state.loadingExamId = null;

      state.latestRequestId =
        state.latestRequestId === null ? null : state.latestRequestId + 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      lecturesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      state.selectedLectureIds = [];
      state.lastFetchedAt = null;
      state.lastFetchedExamId = null;
      state.loadingExamId = null;
      state.latestRequestId = null;
    });
  },
});

export const {
  lecturesLoading,
  lecturesRequestAbandoned,
  lecturesReceived,
  lecturesReceivedForExam,
  lecturesUpsertMany,
  lecturesUpsertManyPreservingSelection,
  lecturesFailed,
  lectureReceived,
  lectureDeleted,
  lecturesRemoved,
  clearLectures,
  lectureSelected,
  lectureActivated,
  lectureDeselected,
  lecturesAllSelected,
  lectureOrderUpdated,
  lectureOrderKeyUpdated,
} = lecturesSlice.actions;

export const lecturesSelectors = lecturesAdapter.getSelectors(
  (state: { lectures: LecturesState }) =>
    state.lectures || lecturesAdapter.getInitialState()
);

// Memoized custom selectors
export const selectSelectedLectureIds = createSelector(
  [(state: { lectures: LecturesState }) => state.lectures.selectedLectureIds],
  (ids) => ids
);

// Memoized custom selectors
export const selectLecturesStatus = createSelector(
  [(state: { lectures: LecturesState }) => state.lectures.status],
  (status) => status
);

export const selectSelectedLectures = createSelector(
  [
    selectSelectedLectureIds,
    (state: { lectures: LecturesState }) => state.lectures.entities,
  ],
  (ids, entities) =>
    ids
      .map((id) => entities[id])
      .filter(
        (lecture): lecture is LecturePreview =>
          Boolean(lecture) &&
          lecture.enabled !== false &&
          lecture.hidden !== true
      )
);

export const selectLecturesLastFetchedAt = (state: {
  lectures: LecturesState;
}) => state.lectures.lastFetchedAt;

export const selectLecturesLastFetchedExamId = (state: {
  lectures: LecturesState;
}) => state.lectures.lastFetchedExamId;

export default lecturesSlice.reducer;
